import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import StatusGroupHeader from 'components/session/SessionStatusGroupHeader';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * Zonas de la lista de clientes: activos, en mora e inactivos.
 *
 * Lo pidió el cliente para revisar la cartera de un vistazo, con el mismo lenguaje visual de
 * "Pendientes por reportar" en sesiones: punto de color, etiqueta y línea. La diferencia es que
 * aquí la lista se pagina en el servidor, así que no se pueden pintar las tres zonas a la vez sin
 * mentir (la página 2 traería otra mezcla). Por eso las zonas funcionan como filtro: se elige una
 * y la lista entera pasa a ser esa zona, con su cabecera de color encima.
 *
 * Los colores salen de `membershipStatus`, tokens propios: los de sesiones están acotados por
 * decisión de diseño a components/session/**.
 */

// El orden es el del ciclo de vida del cobro: al día, se atrasó, se perdió.
export const STATUS_ZONES = [
  { key: 'ACTIVE', token: 'active', i18n: 'membershipStatus.active_male', def: 'Activos', counter: 'active' },
  { key: 'IN_ARREARS', token: 'inArrears', i18n: 'membershipStatus.inArrears', def: 'En mora', counter: 'inArrears' },
  { key: 'INACTIVE', token: 'inactive', i18n: 'membershipStatus.inactive_male', def: 'Inactivos', counter: 'inactive' }
];

function ClientStatusZones({ active = null, counters = null, onChange }) {
  const { t } = useTranslation(['options', 'labels']);
  const theme = useTheme();

  const zone = STATUS_ZONES.find((z) => z.key === active);
  const colorOf = (token) => theme.palette.membershipStatus[token].fg;
  const backgroundOf = (token) => theme.palette.membershipStatus[token].bg;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: zone ? 1 : 0 }}>
        <ZoneChip
          label={t('allZones', { ns: 'labels', defaultValue: 'Todos' })}
          count={null}
          selected={!active}
          color={theme.palette.text.secondary}
          onClick={() => onChange(null)}
        />
        {STATUS_ZONES.map((z) => (
          <ZoneChip
            key={z.key}
            label={t(z.i18n, { ns: 'options', defaultValue: z.def })}
            count={counters?.[z.counter]}
            selected={active === z.key}
            color={colorOf(z.token)}
            background={backgroundOf(z.token)}
            onClick={() => onChange(active === z.key ? null : z.key)}
          />
        ))}
      </Stack>

      {zone && (
        <StatusGroupHeader
          label={t(zone.i18n, { ns: 'options', defaultValue: zone.def })}
          color={colorOf(zone.token)}
          first
        />
      )}
    </Box>
  );
}

ClientStatusZones.propTypes = {
  active: PropTypes.string,
  counters: PropTypes.object,
  onChange: PropTypes.func.isRequired
};

/** Chip pulsable de una zona: etiqueta, contador y color del estado. */
function ZoneChip({ label, count = null, selected, color, background = '', onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        px: 1.75,
        py: 0.75,
        borderRadius: '999px',
        border: '1px solid',
        borderColor: selected ? color : 'hairline.main',
        backgroundColor: selected ? background || 'surface.overlay' : 'transparent',
        transition: `border-color 200ms ${EASE}, background-color 200ms ${EASE}, transform 200ms ${EASE}`,
        '&:hover': { borderColor: color },
        '&:active': { transform: 'scale(0.98)' }
      }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: selected ? color : 'text.secondary' }}>
          {label}
        </Typography>
        {count != null && (
          <Typography variant="caption" sx={{ fontWeight: 800, color: selected ? color : 'text.primary' }}>
            {count}
          </Typography>
        )}
      </Stack>
    </ButtonBase>
  );
}

ZoneChip.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number,
  selected: PropTypes.bool.isRequired,
  color: PropTypes.string.isRequired,
  background: PropTypes.string,
  onClick: PropTypes.func.isRequired
};

export default ClientStatusZones;
